// src/ui/modal.js
import { createWizardForm } from "./wizard.js";

export function createModal({ title = "קביעת תור" } = {}) {
  const overlay = document.createElement("div");
  overlay.className = "lior-booking-modal-overlay";
  
  const box = document.createElement("div");
  box.className = "lior-booking-modal";
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-modal", "true");
  box.setAttribute("aria-hidden", "true");
  box.setAttribute("dir", "rtl");

  // Header with close button
  const header = document.createElement("header");
  const h2 = document.createElement("h2");
  h2.textContent = title;
  
  const desc = document.createElement("p");
  desc.className = "lior-booking-modal-description";
  desc.textContent = "ממלאים 4 פרטים וסיימנו את ההזמנה.";
  
  const closeBtn = document.createElement("button");
  closeBtn.className = "lior-booking-close";
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "סגירת חלון");
  closeBtn.innerHTML = "×";
  
  header.appendChild(h2);
  header.appendChild(desc);
  header.appendChild(closeBtn);
  box.appendChild(header);

  // Form Container
  const formContainer = document.createElement("div");
  formContainer.className = "lior-booking-form";
  
  // Wizard Form
  const { container: wizardContainer, form, submitButton, getFormData, reset } = createWizardForm();
  formContainer.appendChild(wizardContainer);
  box.appendChild(formContainer);

  // Success Screen (hidden by default)
  const successScreen = document.createElement("div");
  successScreen.className = "lior-booking-success-screen";
  successScreen.style.display = "none";
  box.appendChild(successScreen);

  // Message area
  const msgEl = document.createElement("div");
  msgEl.className = "lior-booking-message";
  box.appendChild(msgEl);

  overlay.appendChild(box);

  let isOpen = false;
  let appointmentData = null;

  function open() {
    isOpen = true;
    overlay.classList.add("is-open");
    box.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    
    // Hide floating button when modal opens
    const floatingButton = document.querySelector(".lior-booking-button");
    if (floatingButton) {
      floatingButton.classList.add("lior-booking-hidden");
    }
    
    // Focus first input
    const firstInput = form.querySelector("input");
    if (firstInput) setTimeout(() => firstInput.focus(), 100);
  }

  function close() {
    isOpen = false;
    overlay.classList.remove("is-open");
    box.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    reset();
    msgEl.className = "lior-booking-message";
    msgEl.textContent = "";
    formContainer.style.display = "block";
    successScreen.style.display = "none";
    appointmentData = null;
    
    // Show floating button when modal closes
    const floatingButton = document.querySelector(".lior-booking-button");
    if (floatingButton) {
      floatingButton.classList.remove("lior-booking-hidden");
    }
  }

  function onSubmit(handler) {
    submitButton.addEventListener("click", async (e) => {
      e.preventDefault();
      msgEl.className = "lior-booking-message";
      msgEl.textContent = "";

      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      
      // Add spinner with better UX
      const spinner = document.createElement("div");
      spinner.className = "lior-booking-spinner";
      submitButton.innerHTML = "";
      submitButton.appendChild(spinner);
      submitButton.appendChild(document.createTextNode(" שומר את התור..."));

      try {
        const data = getFormData();
        appointmentData = data;
        await handler(data);
      } catch (error) {
        submitButton.disabled = false;
        submitButton.innerHTML = originalText;
        
        // Check if it's a network error
        const isNetworkError = !navigator.onLine || 
          error.message?.includes('network') || 
          error.message?.includes('fetch') ||
          error.code === 'unavailable';
        
        showError("אירעה שגיאה, נסה שוב מאוחר יותר", isNetworkError);
        throw error;
      }
    });
  }
  
  function formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    const days = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"];
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = date.getMonth() + 1;
    return `${dayName} · ${day}.${month}`;
  }
  
  function formatTime(timeStr) {
    if (!timeStr) return "";
    return timeStr;
  }
  
  function formatPhone(phone) {
    if (!phone) return "";
    return phone;
  }

  function showSuccess(text) {
    if (appointmentData) {
      // Show success screen
      formContainer.style.display = "none";
      successScreen.style.display = "block";
      
      successScreen.innerHTML = `
        <div class="lior-booking-success-icon">✅</div>
        <div class="lior-booking-success-title">התור נקבע בהצלחה!</div>
        <div class="lior-booking-success-card">
          <div class="lior-booking-success-row">
            <span class="lior-booking-success-label">תאריך ושעה</span>
            <span class="lior-booking-success-value">${formatDate(appointmentData.date)} · ${formatTime(appointmentData.time)}</span>
          </div>
          <div class="lior-booking-success-row">
            <span class="lior-booking-success-label">על שם</span>
            <span class="lior-booking-success-value">${appointmentData.name}</span>
          </div>
          <div class="lior-booking-success-row">
            <span class="lior-booking-success-label">טלפון</span>
            <span class="lior-booking-success-value">${formatPhone(appointmentData.phone)}</span>
          </div>
        </div>
        <div style="display: flex; gap: var(--lb-space-sm); margin-top: var(--lb-space-lg);">
          <button class="lior-booking-secondary" onclick="this.closest('.lior-booking-modal').querySelector('.lior-booking-close').click()" style="flex: 1;">
            סגור
          </button>
          <button class="lior-booking-submit" onclick="location.reload()" style="flex: 1; margin-top: 0;">
            קבע תור נוסף
          </button>
        </div>
      `;
    } else {
      // Fallback to message
      msgEl.className = "lior-booking-message success";
      msgEl.innerHTML = `
        <span aria-hidden="true">✅</span>
        <span>${text}</span>
      `;
      setTimeout(() => {
        if (isOpen) {
          close();
        }
      }, 2000);
    }
    
    // Show toast
    showToast(`✔ נקבע תור ל-${appointmentData?.name || "לקוח"} ל-${formatDate(appointmentData?.date || "")} בשעה ${formatTime(appointmentData?.time || "")}`);
  }

  function showError(text, isNetworkError = false) {
    formContainer.style.display = "block";
    successScreen.style.display = "none";
    
    msgEl.className = "lior-booking-message error";
    if (isNetworkError) {
      msgEl.innerHTML = `
        <span aria-hidden="true">⚠️</span>
        <div style="display: flex; flex-direction: column; gap: var(--lb-space-xs); text-align: start;">
          <span style="font-weight: 600;">משהו נתקע</span>
          <span style="font-size: 0.875rem; opacity: 0.9;">תנסה שוב או דבר איתנו בוואטסאפ</span>
        </div>
      `;
    } else {
      msgEl.innerHTML = `
        <span aria-hidden="true">❌</span>
        <span>${text}</span>
      `;
    }
  }
  
  function showToast(message) {
    const toast = document.createElement("div");
    toast.className = "lior-booking-toast success";
    toast.innerHTML = `
      <span aria-hidden="true">✅</span>
      <span>${message}</span>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.classList.add("show"), 10);
    
    setTimeout(() => {
      toast.classList.remove("show");
      setTimeout(() => toast.remove(), 200);
    }, 4000);
  }

  // Close on overlay click
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  // Close on close button
  closeBtn.addEventListener("click", close);

  // Close on Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen) close();
  });

  return { container: overlay, open, close, onSubmit, showSuccess, showError };
}
